import api from "../../api";

// Owner 2026-10-12: a COA is entered as the time the person is AWAY ("8:00 to 10:00 AM"),
// the way people think about it. The record (and payroll, which credits
// ModifiedEnd - ModifiedStart as the hours worked) keeps the legacy meaning: the hours
// the person WILL work that day. These helpers convert both ways against the standard
// 8:00 AM - 4:30 PM day (owner rule: working day 8:00 - 4:30).
// Owner 2026-10-15: a hourly person may have their OWN hours (Team Work Hours, e.g. 7:00
// to 3:00); the away window is then measured against those, not the company day.
export const DAY_START = "08:00";
export const DAY_END = "16:30";

const pick = (o, ...keys) => { for (const k of keys) if (o && o[k] != null) return o[k]; return undefined; };

/** The person's own hours in force on a date ({ start, end } as "HH:mm"), or null = company hours. */
export const fetchDayHours = async (empId, date) => {
    if (!empId || !date) return null;
    try {
        const { data } = await api.get(`attendance/work-window/${encodeURIComponent(String(empId).trim())}`);
        const rows = pick(data, "rows", "Rows") || [];
        const row = rows.find((r) => {
            const from = String(pick(r, "effectiveFrom", "EffectiveFrom") || "").slice(0, 10);
            const to = pick(r, "effectiveTo", "EffectiveTo");
            return from && from <= date && (!to || String(to).slice(0, 10) >= date);
        });
        if (!row) return null;
        const start = String(pick(row, "startTime", "StartTime") || "").slice(0, 5);
        const end = String(pick(row, "endTime", "EndTime") || "").slice(0, 5);
        return start && end ? { start, end } : null;
    } catch {
        return null;   // not allowed to see them or the table is missing: company hours
    }
};

/** "[Away 7:00 AM - 9:00 AM]" in a comment → { awayFrom: "07:00", awayUntil: "09:00" }, else null. */
export const awayFromNote = (comment) => {
    const m = /\[Away\s+(\d{1,2}):(\d{2})\s*([AP]M)\s*-\s*(\d{1,2}):(\d{2})\s*([AP]M)\]/i.exec(comment || "");
    if (!m) return null;
    const to24 = (h, mm, ap) => {
        let hh = parseInt(h, 10) % 12;
        if (ap.toUpperCase() === "PM") hh += 12;
        return `${String(hh).padStart(2, "0")}:${mm}`;
    };
    return { awayFrom: to24(m[1], m[2], m[3]), awayUntil: to24(m[4], m[5], m[6]) };
};

export const toMinutes = (hhmm) => {
    if (!hhmm || typeof hhmm !== "string") return null;
    const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
};
const pad = (n) => String(n).padStart(2, "0");
export const fromMinutes = (min) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
export const pretty = (hhmm) => {
    const min = toMinutes(hhmm);
    if (min == null) return "";
    const h = Math.floor(min / 60), m = min % 60;
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${pad(m)} ${h < 12 ? "AM" : "PM"}`;
};

/** The hours worked that day for an away window. Returns null when the window is not usable. */
export const awayToWorked = (awayFrom, awayUntil, dayStart = DAY_START, dayEnd = DAY_END) => {
    const s = toMinutes(awayFrom), e = toMinutes(awayUntil);
    const ds = toMinutes(dayStart || DAY_START), de = toMinutes(dayEnd || DAY_END);
    if (s == null || e == null || e <= s) return null;
    if (s <= ds) {
        // away at the start of the day: comes in when the absence ends
        return { start: fromMinutes(Math.max(e, ds)), end: fromMinutes(de) };
    }
    if (e >= de) {
        // away until the end of the day: leaves when the absence starts
        return { start: fromMinutes(ds), end: fromMinutes(Math.min(s, de)) };
    }
    // away in the middle of the day: the same hours are missing, so the record ends
    // that much earlier (payroll credits the difference, which is what matters)
    return { start: fromMinutes(ds), end: fromMinutes(de - (e - s)) };
};

/** The away window a stored record most likely meant; null when it cannot be told. */
export const workedToAway = (start, end, dayStart = DAY_START, dayEnd = DAY_END) => {
    const s = toMinutes(start), e = toMinutes(end);
    const ds = toMinutes(dayStart || DAY_START), de = toMinutes(dayEnd || DAY_END);
    if (s == null || e == null) return null;
    if (s > ds && e >= de) return { awayFrom: fromMinutes(ds), awayUntil: fromMinutes(s) };
    if (s <= ds && e < de) return { awayFrom: fromMinutes(e), awayUntil: fromMinutes(de) };
    return null;
};
